import api from "@/lib/api";

const DB_NAME = "ojent-offline";
const DB_VERSION = 1;
const STORE_PENDING = "pending_orders";
const STORE_CACHE = "bootstrap";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_PENDING)) {
        db.createObjectStore(STORE_PENDING, { keyPath: "client_id" });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export function isOnline() {
  return typeof navigator !== "undefined" ? navigator.onLine : true;
}

export function makeClientId() {
  return `off_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export async function stashPendingOrder(order) {
  const client_id = order.client_id || makeClientId();
  const record = {
    ...order,
    client_id,
    queued_at: new Date().toISOString(),
  };
  const db = await openDb();
  const tx = db.transaction(STORE_PENDING, "readwrite");
  tx.objectStore(STORE_PENDING).put(record);
  await txDone(tx);
  db.close();
  return record;
}

export async function listPendingOrders() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PENDING, "readonly");
    const req = tx.objectStore(STORE_PENDING).getAll();
    req.onsuccess = () => {
      db.close();
      resolve(req.result || []);
    };
    req.onerror = () => {
      db.close();
      reject(req.error);
    };
  });
}

export async function removePendingOrder(clientId) {
  const db = await openDb();
  const tx = db.transaction(STORE_PENDING, "readwrite");
  tx.objectStore(STORE_PENDING).delete(clientId);
  await txDone(tx);
  db.close();
}

export async function pendingCount() {
  const list = await listPendingOrders();
  return list.length;
}

export async function cacheBootstrap(data) {
  const db = await openDb();
  const tx = db.transaction(STORE_CACHE, "readwrite");
  tx.objectStore(STORE_CACHE).put({ key: "bootstrap", ...data, cached_at: new Date().toISOString() });
  await txDone(tx);
  db.close();
}

export async function getBootstrapCache() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, "readonly");
    const req = tx.objectStore(STORE_CACHE).get("bootstrap");
    req.onsuccess = () => {
      db.close();
      resolve(req.result || null);
    };
    req.onerror = () => {
      db.close();
      reject(req.error);
    };
  });
}

export async function fetchAndCacheBootstrap() {
  const { data } = await api.get("/offline/bootstrap");
  await cacheBootstrap(data);
  return data;
}

/** Sync queued offline orders to the server. Returns { synced, results, remaining }. */
export async function syncPending() {
  if (!isOnline()) return { synced: 0, results: [], remaining: await pendingCount() };
  const pending = await listPendingOrders();
  if (!pending.length) return { synced: 0, results: [], remaining: 0 };

  const { data } = await api.post("/offline/sync", { orders: pending });
  for (const r of data.results || []) {
    if (r.status === "created" || r.status === "duplicate") {
      if (r.client_id) await removePendingOrder(r.client_id);
    }
  }
  return {
    synced: data.synced || 0,
    results: data.results || [],
    remaining: await pendingCount(),
  };
}
